import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Wind, ArrowRight } from "lucide-react";
import { Reveal, MaskedLine, PRICE } from "./shared";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const FinalCTA = ({ onBuy }) => {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const subscribe = async (e) => {
    e.preventDefault();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      toast.error("Enter a valid email address");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/leads`, { email });
      toast.success("You're on the drop list. Watch your inbox.");
      setEmail("");
    } catch {
      toast.error("Could not save your email. Try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section data-testid="final-cta-section" className="relative overflow-hidden border-t border-steel">
      <div className="spotlight absolute inset-0" aria-hidden="true" />
      <div className="relative mx-auto max-w-[1600px] px-5 py-28 md:px-10 md:py-44">
        <Reveal>
          <p className="flex items-center gap-3 font-mono text-xs tracking-[0.35em] uppercase text-smoke">
            <Wind className="h-4 w-4 text-volt" /> Last call
          </p>
        </Reveal>
        <h2 className="mt-8 font-display font-black uppercase leading-[0.9] tracking-tighter">
          <MaskedLine delay={0.1} className="text-[10vw] md:text-[7vw]">Air cans</MaskedLine>
          <MaskedLine delay={0.25} className="text-[10vw] md:text-[7vw]">
            are <span className="text-volt">dead.</span>
          </MaskedLine>
        </h2>
        <Reveal delay={0.2}>
          <p className="mt-8 max-w-xl text-base text-smoke md:text-lg">
            Join the drop list for restock alerts and launch discounts — or skip the line and
            grab the X8 now at ${PRICE}.
          </p>
        </Reveal>

        <Reveal delay={0.3}>
          <form onSubmit={subscribe} className="mt-10 flex max-w-xl flex-col gap-3 sm:flex-row" data-testid="email-capture-form">
            <input
              data-testid="email-capture-input"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@email.com"
              className="flex-1 border border-steel bg-carbon px-5 py-4 font-mono text-sm text-foreground placeholder:text-smoke/60 focus:border-volt focus:outline-none"
            />
            <button
              data-testid="email-capture-submit"
              type="submit"
              disabled={loading}
              className="group flex items-center justify-center gap-2 border border-volt px-8 py-4 font-mono text-sm font-bold tracking-[0.2em] uppercase text-volt transition-colors duration-200 hover:bg-volt hover:text-ink disabled:opacity-50"
            >
              {loading ? "Joining…" : "Join drop list"}
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </button>
          </form>
        </Reveal>

        <Reveal delay={0.4}>
          <button
            data-testid="final-buy-button"
            onClick={onBuy}
            className="mt-6 border border-volt bg-volt px-10 py-5 font-mono text-sm font-bold tracking-[0.2em] uppercase text-ink transition-colors duration-200 hover:bg-transparent hover:text-volt"
          >
            Buy the X8 — ${PRICE}
          </button>
        </Reveal>
      </div>

      <footer className="relative border-t border-steel" data-testid="site-footer">
        <div className="mx-auto flex max-w-[1600px] flex-col gap-4 px-5 py-8 md:flex-row md:items-center md:justify-between md:px-10">
          <span className="font-display text-xs font-bold uppercase tracking-widest">
            X8<span className="text-volt">/</span>Violent Fan
          </span>
          <p className="font-mono text-[10px] tracking-[0.2em] uppercase text-smoke">
            Secure card &amp; PayPal checkout · Ships in 24–48h · 30-day money-back guarantee.
          </p>
          <p className="font-mono text-[10px] tracking-[0.2em] uppercase text-smoke">
            © 2026 — Built to convert
          </p>
        </div>
      </footer>
    </section>
  );
};
